import { createContext, useContext, useState, useEffect } from 'react'
import { authAPI } from '../services/api'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  // Try to restore user from localStorage on mount
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user')
      return storedUser ? JSON.parse(storedUser) : null
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(true)
  const [justLoggedIn, setJustLoggedIn] = useState(false)
  const [loginTimestamp, setLoginTimestamp] = useState(() => {
    const stored = localStorage.getItem('loginTimestamp')
    return stored ? parseInt(stored) : null
  })

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      try {
        localStorage.setItem('user', JSON.stringify(user))
      } catch (err) {
        console.warn('Failed to save user to localStorage:', err)
      }
    } else {
      localStorage.removeItem('user')
    }
  }, [user])

  useEffect(() => {
    // Get CSRF token first, then check auth
    const initializeAuth = async () => {
      try {
        // Fetch CSRF token to ensure it's available
        await authAPI.getCsrfToken()
      } catch (error) {
        console.warn('Could not fetch CSRF token:', error)
      }
      
      // Use a small delay to ensure state is set after login
      setTimeout(() => {
        // Check if we have a user (from state or localStorage) or just logged in
        const storedUser = localStorage.getItem('user')
        const hasStoredUser = storedUser !== null
        const storedTimestamp = localStorage.getItem('loginTimestamp')
        const recentLogin = storedTimestamp && (Date.now() - parseInt(storedTimestamp) < 60000)
        
        // If we have a user (in state or localStorage) or just logged in, don't check auth immediately
        if (user || hasStoredUser || justLoggedIn || recentLogin) {
          // If we have stored user but not in state, restore it
          if (!user && hasStoredUser) {
            try {
              const parsedUser = JSON.parse(storedUser)
              setUser(parsedUser)
              console.log('Restored user from localStorage:', parsedUser)
            } catch (err) {
              console.warn('Failed to parse stored user:', err)
            }
          }
          setLoading(false)
          // Verify session in background without blocking
          if (user || hasStoredUser) {
            checkAuth().catch(err => {
              console.warn('Background auth check failed, but keeping user state:', err)
            })
          }
        } else {
          // No user found, check auth
          checkAuth()
        }
      }, 100)
    }
    initializeAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkAuth = async () => {
    try {
      const response = await authAPI.getCurrentUser()
      console.log('checkAuth response:', response.data)
      setUser(response.data)
      setJustLoggedIn(false) // Reset flag after successful auth check
      setLoginTimestamp(null) // Clear timestamp after successful check
      localStorage.removeItem('loginTimestamp') // Clear from localStorage
    } catch (error) {
      console.log('checkAuth failed:', error.response?.status, error.message)
      const storedTimestamp = localStorage.getItem('loginTimestamp')
      const timestampValue = loginTimestamp || (storedTimestamp ? parseInt(storedTimestamp) : null)
      const recentLogin = timestampValue && (Date.now() - timestampValue < 300000) // Extended to 5 minutes
      
      // If checkAuth fails with 403/401, we're not authenticated
      if (error.response?.status === 403 || error.response?.status === 401) {
        // Only keep user state if we just logged in (cookies might be propagating)
        if (justLoggedIn || recentLogin) {
          console.warn('checkAuth failed but keeping user state (recent login - cookies may be propagating)')
        } else {
          // Clear user state - we're not authenticated
          console.log('Clearing user state - checkAuth returned 403/401 and no recent login')
          setUser(null)
          localStorage.removeItem('user')
          localStorage.removeItem('loginTimestamp')
        }
      } else {
        // For other errors, keep user state
        console.warn('checkAuth failed with non-auth error, keeping user state')
      }
    } finally {
      setLoading(false)
    }
  }

  const login = async (username, password) => {
    try {
      // First, get CSRF token if not already available
      let csrfToken = getCookie('csrftoken')
      if (!csrfToken) {
        try {
          console.log('[Auth] Fetching CSRF token...')
          const csrfResponse = await authAPI.getCsrfToken()
          console.log('[Auth] CSRF token response:', csrfResponse.data)
          // Try to get token from response if cookie wasn't set
          if (csrfResponse.data?.csrfToken) {
            csrfToken = csrfResponse.data.csrfToken
            console.log('[Auth] Got CSRF token from response body')
          }
          // Also check if cookie was set (might work for same-domain)
          if (!csrfToken) {
            await new Promise(resolve => setTimeout(resolve, 100))
            csrfToken = getCookie('csrftoken')
          }
          console.log('[Auth] CSRF token after fetch:', csrfToken ? 'Found' : 'Still missing')
          console.log('[Auth] All cookies:', document.cookie)
        } catch (csrfError) {
          console.error('[Auth] Could not fetch CSRF token:', csrfError)
        }
      } else {
        console.log('[Auth] Using existing CSRF token')
      }

      // Now attempt login
      const response = await authAPI.login(username, password)
      console.log('Login response:', response.data)
      
      // Store user from login response immediately - don't wait for checkAuth
      if (response.data.success) {
        // Set flags to prevent checkAuth from clearing user state
        setJustLoggedIn(true)
        const now = Date.now()
        setLoginTimestamp(now)
        // Store in localStorage as backup
        localStorage.setItem('loginTimestamp', now.toString())
        
        // CRITICAL: Store user from login response immediately
        if (response.data.user) {
          console.log('✅ Login successful, storing user:', response.data.user)
          const now = Date.now()
          setUser(response.data.user)
          setLoading(false)
          
          // Store timestamp in localStorage as backup
          localStorage.setItem('loginTimestamp', now.toString())
          
          // Don't call checkAuth for at least 60 seconds after login
          // This prevents the redirect loop caused by cookie propagation delays
          setTimeout(() => {
            // Only verify if we still have the user
            setUser((currentUser) => {
              if (currentUser && currentUser.id === response.data.user.id) {
                // Verify session in background
                checkAuth().then(() => {
                  console.log('✅ Session verified successfully')
                  setJustLoggedIn(false)
                  setLoginTimestamp(null)
                  localStorage.removeItem('loginTimestamp')
                }).catch(err => {
                  // Don't clear user if checkAuth fails - cookies might still be propagating
                  console.warn('⚠️ Session verification failed, but keeping user state:', err)
                  // Keep protection for longer
                  setTimeout(() => {
                    setJustLoggedIn(false)
                    setLoginTimestamp(null)
                    localStorage.removeItem('loginTimestamp')
                  }, 60000)
                })
              }
              return currentUser
            })
          }, 60000) // Wait 60 seconds before verification
          
          return { success: true, user: response.data.user }
        }
        
        // If no user in response, this is an error
        console.error('❌ No user data in login response')
        return { success: false, error: 'Login succeeded but user data not received' }
      }
      return { success: false, error: response.data.message || 'Login failed' }
    } catch (error) {
      console.error('Login error:', error)
      // Handle 405 Method Not Allowed - likely a server configuration issue
      if (error.response?.status === 405) {
        return { 
          success: false, 
          error: 'Server configuration error. Please contact support or try again later.' 
        }
      }
      // Handle CSRF errors specifically
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('CSRF')) {
        return {
          success: false,
          error: 'CSRF validation failed. Please refresh the page and try again.',
        }
      }
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Login failed',
      }
    }
  }

  // Helper function to get cookie
  function getCookie(name) {
    let cookieValue = null
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';')
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim()
        if (cookie.substring(0, name.length + 1) === name + '=') {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
          break
        }
      }
    }
    return cookieValue
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      setJustLoggedIn(false)
      setLoginTimestamp(null)
      localStorage.removeItem('loginTimestamp')
      localStorage.removeItem('user') // Clear stored user
      window.location.href = '/login'
    }
  }

  // Check if user is superuser (bypasses all checks)
  const isSuperuser = user?.is_superuser || false

  // Get user groups for direct group-based access
  const userGroups = user?.groups || []

  // Role helpers
  const isAdmin = isSuperuser || userGroups.includes('Admin')
  const isFinanceManager = userGroups.includes('Finance Manager')
  const isManager = isAdmin || isFinanceManager || userGroups.includes('Manager')
  const hasAdminAccess = isSuperuser || userGroups.includes('Admin') // Only Admin and Superuser have Admin Panel access

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isSuperuser, // Superuser bypasses all permission checks
    userGroups, // Direct access to user groups for group-based permissions
    isAdmin,
    isFinanceManager,
    isManager,
    hasAdminAccess,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

